import React, { useEffect } from 'react';
import AOS from 'aos';
import 'aos/dist/aos.css';
import FloatingWidgets from '@/components/layout/FloatingWidgets';
import CookieConsentBanner from '@/components/layout/CookieConsentBanner';
import { Toaster } from "@/components/ui/toaster"
import { Toaster as HotToaster } from 'react-hot-toast'
import { Toaster as SonnerToaster } from 'sonner'
import { QueryClientProvider } from '@tanstack/react-query'
import { queryClientInstance } from '@/lib/query-client'
import { BrowserRouter as Router, Route, Routes, useLocation, Navigate, Outlet, useParams } from 'react-router-dom';
import PageNotFound from './lib/PageNotFound';
import { AuthProvider, useAuth } from '@/lib/AuthContext';
import UserNotRegisteredError from '@/components/UserNotRegisteredError';
import AppLayout from '@/components/layout/AppLayout';
import PortalGuard from '@/components/layout/PortalGuard';
import { SessionProvider } from '@/lib/SessionContext';
import { AsaConfirmProvider } from '@/contexts/AsaConfirmContext';
import { AsaProvider } from '@/context/AsaContext';
import { AsaAssistantWidget } from '@/Components/ui/AsaAssistantWidget';
import { loadFaceApiModels } from '@/utils/faceApiLoader';

// New Pages
import LandingPage from '@/pages/LandingPage';
import AboutPage from '@/pages/AboutPage';
import ContactPage from '@/pages/ContactPage';
import PartnerPage from '@/pages/PartnerPage';
import SkincarePage from '@/pages/SkincarePage';
import ParfumPage from '@/pages/ParfumPage';
import MaklonKosmetikPage from '@/pages/MaklonKosmetikPage';
import FormulasiPage from '@/pages/FormulasiPage';
import DesainKemasanPage from '@/pages/DesainKemasanPage';
import LegalitasPage from '@/pages/LegalitasPage';
import ArticlePage from '@/pages/ArticlePage';
import ArticleDetailPage from '@/pages/ArticleDetailPage';
import PricingSkincarePage from '@/pages/PricingSkincarePage';
import PricingParfumPage from '@/pages/PricingParfumPage';
import ClientLayout from '@/components/layout/ClientLayout';
import ClientLogin from '@/pages/ClientLogin';
import ClientPOForm from '@/pages/ClientPOForm';
import ClientPOHistory from '@/pages/ClientPOHistory';
import ClientPODetail from '@/pages/ClientPODetail';
import ClientArtwork from '@/pages/ClientArtwork';
import ClientDatabaseProduk from '@/pages/ClientDatabaseProduk';
import ClientProfile from '@/pages/ClientProfile';
import ClientOnboarding from '@/pages/ClientOnboarding';
import MarketingPortalControlPage from '@/pages/marketing/MarketingPortalControlPage';
import PublicSuratJalanPage from '@/pages/PublicSuratJalanPage';

import ProductionDashboardMain from '@/pages/production/ProductionDashboardMain';
import ProductionPOList from '@/pages/_legacy/Dashboard';
import FinanceDashboard from '@/pages/FinanceDashboard';
import TeamLogin from '@/pages/TeamLogin';

// Internal Production Portal Subpages
import ProductionHPP from '@/pages/StokHPP';
import StokItemDetail from '@/pages/StokItemDetail';
import ProductionSample from '@/pages/SampleRacikanPage';
import ProductionInventory from '@/pages/_legacy/Inventory';
import ProductionDelivery from '@/pages/_legacy/DeliveryPage';
import ProductionDone from '@/pages/_legacy/Shipped';
import ProductionReturn from '@/pages/_legacy/Return';
import ProductionReports from '@/pages/_legacy/Reporting';
import ProductionTeam from '@/pages/_legacy/TimProduksiPage';
import ProductionTasks from '@/pages/_legacy/TugasPage';
import ProductionSettings from '@/pages/_legacy/PengaturanPerusahaan';
import ProductionProfile from '@/pages/_legacy/ProfilSaya';
import ProductionDetailPage from '@/pages/ProductionDetail';
import DatabaseBrand from '@/pages/production/DatabaseBrand';
import RejectTracking from '@/pages/production/RejectTracking';
import OfficeGateway from '@/pages/OfficeGateway';

// Workspace Portal Subpages
import WorkspaceDashboard from '@/pages/workspace/WorkspaceDashboard';
import WorkspaceAbsensi from '@/pages/workspace/WorkspaceAbsensi';
import WorkspaceClients from '@/pages/workspace/WorkspaceClients';
import WorkspaceTeam from '@/pages/workspace/WorkspaceTeam';
import WorkspaceBackup from '@/pages/workspace/WorkspaceBackup';
import WorkspaceSettings from '@/pages/workspace/WorkspaceSettings';
import WorkspacePageAccess from '@/pages/workspace/WorkspacePageAccess';
import WorkspaceNotificationSettings from '@/pages/workspace/WorkspaceNotificationSettings';
import WorkspaceActivityLog from '@/pages/workspace/WorkspaceActivityLog';
import WorkspaceAI from '@/pages/workspace/WorkspaceAI';
import WorkspaceWebHealth from '@/pages/workspace/WorkspaceWebHealth';
import SimulasiBiayaProduksi from '@/pages/workspace/SimulasiBiayaProduksi';
import WorkspaceDIPGenerator from '@/pages/workspace/WorkspaceDIPGenerator';
import WorkspaceHistory from '@/pages/workspace/WorkspaceHistory';
import WorkspaceHistoryInvoice from '@/pages/workspace/WorkspaceHistoryInvoice';
import WorkspaceHistoryPurchasing from '@/pages/workspace/WorkspaceHistoryPurchasing';
import WorkspaceHistoryClientPO from '@/pages/workspace/WorkspaceHistoryClientPO';
import WorkspaceBatchDatabase from '@/pages/workspace/WorkspaceBatchDatabase';

// Finance Portal Subpages
import FinanceDashboardMain from '@/pages/finance/FinanceDashboardMain';
import FinanceInvoices from '@/pages/finance/FinanceInvoices';
import FinanceCustomInvoices from '@/pages/finance/FinanceCustomInvoices';
import FinanceCalculator from '@/pages/finance/FinanceCalculator';
import FinanceReports from '@/pages/finance/FinanceReports';
import FinanceAccounts from '@/pages/finance/FinanceAccounts';
import FinanceOperational from '@/pages/finance/FinanceOperational';
import FinanceLaporan from '@/pages/finance/FinanceLaporan';
import FinanceOmset from '@/pages/finance/FinanceOmset';
import LaporanPPN from '@/pages/finance/LaporanPPN';
import FinancePayroll from '@/pages/finance/FinancePayroll';
import FinanceProductionCosts from '@/pages/finance/FinanceProductionCosts';
import PayablesReceivables from '@/pages/finance/PayablesReceivables';
import ProfitSample from './pages/finance/ProfitSample';

// New Finance Accounting Routes
import FinanceChartOfAccounts from '@/pages/finance/FinanceChartOfAccounts';
import FinanceLedgerDetail from '@/pages/finance/FinanceLedgerDetail';
import FinanceReportsHub from '@/pages/finance/FinanceReportsHub';
import FinanceReportView from '@/pages/finance/FinanceReportView';
import FinanceAssets from '@/pages/finance/FinanceAssets';

// Analytics Portal Subpages
import AnalyticsDashboard from '@/pages/analytics/AnalyticsDashboard';
import MarginKeseluruhan from '@/pages/analytics/MarginKeseluruhan';
import LaporanKeuangan from '@/pages/analytics/LaporanKeuangan';
import MarginCustom from '@/pages/analytics/MarginCustom';

// Marketing Portal Subpages
import MarketingDashboard from '@/pages/marketing/MarketingDashboard';
import ClientData from '@/pages/marketing/ClientData';
import MarketingRepeatOrder from '@/pages/marketing/MarketingRepeatOrder';
import RankingOmset from '@/pages/marketing/RankingOmset';
import MarketingSPHList from '@/pages/marketing/MarketingSPHList';

// Purchasing Portal Subpages
import PurchasingDashboard from '@/pages/purchasing/PurchasingDashboard';
import PurchasingSupplierDatabase from '@/pages/purchasing/PurchasingSupplierDatabase';
import PurchasingDataPemesan from '@/pages/purchasing/PurchasingDataPemesan';
import PurchasingLowStock from '@/pages/purchasing/PurchasingLowStock';
import PurchasingItemsToBuy from '@/pages/purchasing/PurchasingItemsToBuy';
import PurchasingItemsPO from '@/pages/purchasing/PurchasingItemsPO';
import PurchasingRequestFunds from '@/pages/purchasing/PurchasingRequestFunds';
import PurchasingDocuments from '@/pages/purchasing/PurchasingDocuments';
import PurchasingInvoiceDetail from '@/pages/purchasing/PurchasingInvoiceDetail';
import { useSession } from '@/lib/SessionContext';

const OfficeLayout = () => {
  const { member } = useSession();
  const location = useLocation();

  if (!member) {
    return <Navigate to={`/login-tim?redirect=${encodeURIComponent(location.pathname)}`} replace />;
  }

  return <Outlet />;
};

const ProductionDetailRedirect = () => {
  const { id } = useParams();
  return <Navigate to={`/office/production/${id}`} replace />;
};

const AuthenticatedApp = () => {
  const { isLoadingAuth, isLoadingPublicSettings, authError, navigateToLogin } = useAuth();
  const location = useLocation();
  const isInternalRoute = location.pathname.startsWith('/office');

  if (isInternalRoute) {
    // Show loading spinner while checking app public settings
    if (isLoadingPublicSettings) {
      return (
        <div className="fixed inset-0 flex items-center justify-center bg-[#FDFBF7]">
          <div className="w-8 h-8 border-4 border-[#D4AF37] border-t-[#302621] rounded-full animate-spin"></div>
        </div>
      );
    }
  }

  // Render the main app
  return (
    <PortalGuard>
      <FloatingWidgets />
      <Routes>
        {/* Public Route */}
      <Route path="/" element={<LandingPage />} />
      <Route path="/tentang-kami" element={<AboutPage />} />
      <Route path="/kontak" element={<ContactPage />} />
      <Route path="/partner" element={<PartnerPage />} />
      <Route path="/produk/skincare" element={<SkincarePage />} />
      <Route path="/produk/parfum" element={<ParfumPage />} />
      <Route path="/layanan/maklon-kosmetik" element={<MaklonKosmetikPage />} />
      <Route path="/layanan/formulasi" element={<FormulasiPage />} />
      <Route path="/layanan/desain-kemasan" element={<DesainKemasanPage />} />
      <Route path="/layanan/legalitas" element={<LegalitasPage />} />
      <Route path="/artikel" element={<ArticlePage />} />
      <Route path="/artikel/:id" element={<ArticleDetailPage />} />
      <Route path="/pricing" element={<Navigate to="/pricing/parfum" replace />} />
      <Route path="/pricing/skincare" element={<PricingSkincarePage />} />
      <Route path="/pricing/parfum" element={<PricingParfumPage />} />
      <Route path="/digitalmarketing" element={<MarketingPortalControlPage />} />
      <Route path="/marketing" element={<Navigate to="/digitalmarketing" replace />} />
      <Route path="/login" element={<ClientLogin />} />
      <Route path="/client-onboarding" element={<ClientOnboarding />} />
      <Route path="/login-tim" element={<TeamLogin />} />
      <Route path="/surat-jalan/:id" element={<PublicSuratJalanPage />} />
      <Route path="/surat-jalan" element={<PublicSuratJalanPage />} />
      
      {/* Production Route Direct Redirects */}
      <Route path="/production" element={<Navigate to="/office/production/po-list" replace />} />
      <Route path="/production/po-list" element={<Navigate to="/office/production/po-list" replace />} />
      <Route path="/production/:id" element={<ProductionDetailRedirect />} />
      
      {/* Client Routes (Using ClientLayout with LandingPage-style Navbar) */}
      <Route element={<ClientLayout />}>
        <Route path="/dashboard-client" element={<Navigate to="/daftar-pesanan" replace />} />
        <Route path="/pesan-po" element={<ClientPOForm />} />
        <Route path="/daftar-pesanan" element={<ClientPOHistory />} />
        <Route path="/pesanan/:id" element={<ClientPODetail onBack={() => window.history.back()} />} />
        <Route path="/database-produk" element={<ClientDatabaseProduk />} />
        <Route path="/artwork" element={<Navigate to="/database-produk" replace />} />
        <Route path="/profil-saya" element={<ClientProfile />} />
      </Route>
      
      {/* Protected Routes nested under /office and guarded by OfficeLayout */}
      <Route path="/office" element={<OfficeLayout />}>
        {/* Hub Gateway */}
        <Route index element={<OfficeGateway />} />
        
        {/* Production, Workspace, and Finance Portals using shared layout */}
        <Route element={<AppLayout />}>
          <Route path="production" element={<ProductionDashboardMain />} />
          <Route path="production/po-list" element={<ProductionPOList />} />
          <Route path="production/hpp" element={<ProductionHPP />} />
          <Route path="production/sample" element={<ProductionSample />} />
          <Route path="production/brands" element={<DatabaseBrand />} />
          <Route path="production/inventory" element={<ProductionHPP />} />
          <Route path="production/stok-detail" element={<StokItemDetail />} />
          <Route path="production/delivery" element={<ProductionDelivery />} />

          <Route path="production/return" element={<ProductionReturn />} />
          <Route path="production/reports" element={<ProductionReports />} />
          <Route path="production/tasks" element={<ProductionTasks />} />
          <Route path="production/reject-tracking" element={<RejectTracking />} />
          <Route path="production/ai" element={<WorkspaceAI />} />
          <Route path="production/profile" element={<ProductionProfile />} />
          <Route path="production/:id" element={<ProductionDetailPage />} />
          
          {/* Workspace Portal wrapped in the same premium layout */}
          <Route path="workspace" element={<WorkspaceDashboard />} />
          <Route path="workspace/absensi" element={<WorkspaceAbsensi />} />
          <Route path="workspace/clients" element={<WorkspaceClients />} />
          <Route path="workspace/team" element={<WorkspaceTeam />} />
          <Route path="workspace/backup" element={<WorkspaceBackup />} />
          <Route path="workspace/settings" element={<WorkspaceSettings />} />
          <Route path="workspace/access" element={<WorkspacePageAccess />} />
          <Route path="workspace/notification-settings" element={<WorkspaceNotificationSettings />} />
          <Route path="workspace/activity-log" element={<WorkspaceActivityLog />} />
          <Route path="workspace/ai" element={<WorkspaceAI />} />
          <Route path="workspace/health" element={<WorkspaceWebHealth />} />
          <Route path="workspace/simulasi-biaya" element={<SimulasiBiayaProduksi />} />
          <Route path="workspace/profile" element={<ProductionProfile />} />
          <Route path="workspace/dip-generator" element={<WorkspaceDIPGenerator />} />
          <Route path="workspace/history" element={<WorkspaceHistory />} />
          <Route path="workspace/history-invoices" element={<WorkspaceHistoryInvoice />} />
          <Route path="workspace/history-purchasing" element={<WorkspaceHistoryPurchasing />} />
          <Route path="workspace/history-client-po" element={<WorkspaceHistoryClientPO />} />
          <Route path="workspace/batch-database" element={<WorkspaceBatchDatabase />} />

          {/* Finance Portal wrapped in the same premium layout */}
          <Route path="finance" element={<FinanceDashboardMain />} />
          <Route path="finance/invoices" element={<FinanceInvoices />} />
          <Route path="finance/custom-invoices" element={<FinanceCustomInvoices />} />
          <Route path="finance/sph-list" element={<MarketingSPHList />} />
          <Route path="finance/calculator" element={<FinanceCalculator />} />
          <Route path="finance/purchasing-requests" element={<PurchasingRequestFunds />} />
          <Route path="finance/inventory" element={<ProductionHPP />} />
          <Route path="finance/assets" element={<FinanceAssets />} />
          <Route path="finance/stok-detail" element={<StokItemDetail />} />
          <Route path="finance/reports" element={<FinanceReports />} />
          <Route path="finance/accounts" element={<FinanceAccounts />} />
          <Route path="finance/operational" element={<FinanceOperational />} />
          <Route path="finance/production-costs" element={<FinanceProductionCosts />} />
          <Route path="finance/payroll" element={<FinancePayroll />} />
          <Route path="finance/laporan" element={<FinanceLaporan />} />
          <Route path="finance/laporan-ppn" element={<LaporanPPN />} />
          <Route path="finance/payables" element={<PayablesReceivables />} />
          <Route path="finance/receivables" element={<PayablesReceivables />} />
          <Route path="finance/profit-sample" element={<ProfitSample />} />
          <Route path="finance/reject-tracking" element={<RejectTracking />} />
          <Route path="finance/ai" element={<WorkspaceAI />} />
          <Route path="finance/profile" element={<ProductionProfile />} />
          <Route path="finance/chart-of-accounts" element={<FinanceChartOfAccounts />} />
          <Route path="finance/ledger/:accountId" element={<FinanceLedgerDetail />} />
          <Route path="finance/reports-hub" element={<FinanceReportsHub />} />
          <Route path="finance/reports-hub/:reportType" element={<FinanceReportView />} />

          {/* Marketing Portal wrapped in the same premium layout */}
          <Route path="marketing" element={<MarketingDashboard />} />
          <Route path="marketing/ranking" element={<RankingOmset />} />
          <Route path="marketing/client-data" element={<ClientData />} />
          <Route path="marketing/client-data/invoice/:poId" element={<PurchasingInvoiceDetail />} />
          <Route path="marketing/calculator" element={<FinanceCalculator />} />
          <Route path="marketing/invoices" element={<FinanceInvoices />} />
          <Route path="marketing/custom-invoices" element={<FinanceCustomInvoices />} />
          <Route path="marketing/sph-list" element={<MarketingSPHList />} />
          <Route path="marketing/sample" element={<ProductionSample />} />
          <Route path="marketing/brands" element={<DatabaseBrand />} />
          <Route path="marketing/inventory" element={<ProductionHPP />} />
          <Route path="marketing/stok-detail" element={<StokItemDetail />} />
          <Route path="marketing/receivables" element={<PayablesReceivables />} />
          <Route path="marketing/profit-sample" element={<ProfitSample />} />
          <Route path="marketing/ai" element={<WorkspaceAI />} />
          <Route path="marketing/tasks-report" element={<ProductionReports />} />
          <Route path="marketing/profile" element={<ProductionProfile />} />

          {/* Purchasing Portal wrapped in the same premium layout */}
          <Route path="purchasing" element={<PurchasingDashboard />} />
          <Route path="purchasing/client-data" element={<ClientData />} />
          <Route path="purchasing/client-data/invoice/:poId" element={<PurchasingInvoiceDetail />} />
          <Route path="purchasing/suppliers" element={<PurchasingSupplierDatabase />} />
          <Route path="purchasing/data-pemesan" element={<PurchasingDataPemesan />} />
          <Route path="purchasing/low-stock" element={<PurchasingLowStock />} />
          <Route path="purchasing/items-to-buy" element={<PurchasingItemsToBuy />} />
          <Route path="purchasing/items-po" element={<PurchasingItemsPO />} />
          <Route path="purchasing/purchasing-requests" element={<PurchasingRequestFunds />} />
          <Route path="purchasing/documents" element={<PurchasingDocuments />} />
          <Route path="purchasing/laporan-ppn" element={<LaporanPPN />} />
          <Route path="purchasing/inventory" element={<ProductionHPP />} />
          <Route path="purchasing/stok-detail" element={<StokItemDetail />} />
          <Route path="purchasing/payables" element={<PayablesReceivables />} />
          <Route path="purchasing/delivery" element={<ProductionDelivery />} />
          <Route path="purchasing/ai" element={<WorkspaceAI />} />
          <Route path="purchasing/profile" element={<ProductionProfile />} />

          {/* Analytics Portal wrapped in the same premium layout */}
          <Route path="analytics" element={<AnalyticsDashboard />} />
          <Route path="analytics/margin-keseluruhan" element={<MarginKeseluruhan />} />
          <Route path="analytics/laporan-keuangan" element={<LaporanKeuangan />} />
          <Route path="analytics/margin-po" element={<FinanceReports />} />
          <Route path="analytics/margin-sample" element={<ProfitSample />} />
          <Route path="analytics/margin-custom" element={<MarginCustom />} />
          <Route path="analytics/ranking" element={<RankingOmset />} />
          <Route path="analytics/omset" element={<FinanceOmset />} />
          <Route path="analytics/laporan-ppn" element={<LaporanPPN />} />
          <Route path="analytics/payables-receivables" element={<PayablesReceivables />} />
          <Route path="analytics/ai" element={<WorkspaceAI />} />
        </Route>
      </Route>

      <Route path="*" element={<PageNotFound />} />
      </Routes>
    </PortalGuard>
  );
};

import { ClientSessionProvider } from '@/lib/ClientSessionContext';
import { GlobalErrorBoundary } from '@/components/shared/GlobalErrorBoundary';

import GlobalLoader from '@/components/layout/GlobalLoader';
import { getPixelConfig, initMetaPixel } from '@/utils/metaPixelTracker';
import { trackCurrentVisitor } from '@/utils/visitorTracker';

function VisitorTrackerBeacon() {
  const location = useLocation();
  
  useEffect(() => {
    trackCurrentVisitor(location.pathname);
    const interval = setInterval(() => {
      trackCurrentVisitor(location.pathname);
    }, 5000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  return null;
}

function App() {
  useEffect(() => {
    AOS.init({
      duration: 800,
      once: true,
      easing: 'ease-out-cubic',
    });

    // Inisialisasi Meta Pixel
    const pixelConf = getPixelConfig();
    if (pixelConf.metaPixelId) {
      initMetaPixel(pixelConf.metaPixelId);
    }

    // Preload model AI wajah di background
    loadFaceApiModels();
  }, []);

  return (
    <AuthProvider>
      <GlobalLoader />
      <QueryClientProvider client={queryClientInstance}>
        <SessionProvider>
          <ClientSessionProvider>
            <Router>
              <VisitorTrackerBeacon />
              <GlobalErrorBoundary>
                <AsaConfirmProvider>
                  <AsaProvider>
                    <AuthenticatedApp />
                    <AsaAssistantWidget />
                    <FloatingWidgets />
                    <CookieConsentBanner />
                  </AsaProvider>
                </AsaConfirmProvider>
              </GlobalErrorBoundary>
            </Router>
            <Toaster />
            <HotToaster position="top-center" />
            <SonnerToaster position="top-right" richColors />
          </ClientSessionProvider>
        </SessionProvider>
      </QueryClientProvider>
    </AuthProvider>
  )
}

export default App